import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Bell, Check, CheckCheck, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import NotificationItem from "@/components/notifications/NotificationItem";
import NotificationDetailModal from "@/components/notifications/NotificationDetailModal";
import { useToast } from "@/hooks/use-toast";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SEO } from "@/components/SEO";

interface AdminNotification {
  id: string;
  title_it: string;
  title_en: string | null;
  title_es: string | null;
  description_it: string | null;
  description_en: string | null;
  description_es: string | null;
  image_url: string | null;
  cta_url: string | null;
  cta_text_it: string | null;
  cta_text_en: string | null;
  cta_text_es: string | null;
  created_at: string;
}

interface UserNotification {
  id: string;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  notification: AdminNotification;
}

export default function Notifications() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "unread" | "read">("all");
  const [selectedNotification, setSelectedNotification] = useState<UserNotification | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (user) {
      loadNotifications();
    }
  }, [user]);

  const loadNotifications = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("user_notifications")
        .select(`
          id,
          is_read,
          read_at,
          created_at,
          notification:admin_notifications (
            id,
            title_it,
            title_en,
            title_es,
            description_it,
            description_en,
            description_es,
            image_url,
            cta_url,
            cta_text_it,
            cta_text_en,
            cta_text_es,
            created_at
          )
        `)
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      const validNotifications = (data || []).filter(
        (n): n is UserNotification => n.notification !== null
      );
      setNotifications(validNotifications);
    } catch (error) {
      console.error("Error loading notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleReadStatus = async (notificationId: string, currentIsRead: boolean) => {
    try {
      const newIsRead = !currentIsRead;
      await supabase
        .from("user_notifications")
        .update({ 
          is_read: newIsRead, 
          read_at: newIsRead ? new Date().toISOString() : null 
        })
        .eq("id", notificationId);

      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId
            ? { ...n, is_read: newIsRead, read_at: newIsRead ? new Date().toISOString() : null }
            : n
        )
      );
    } catch (error) {
      console.error("Error toggling notification read status:", error);
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      await supabase
        .from("user_notifications")
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq("id", notificationId);

      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId
            ? { ...n, is_read: true, read_at: new Date().toISOString() }
            : n
        )
      );
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    if (!user) return;

    try {
      await supabase
        .from("user_notifications")
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq("user_id", user.id)
        .eq("is_read", false);

      setNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          is_read: true,
          read_at: n.read_at || new Date().toISOString(),
        }))
      );

      toast({
        title: t("notifications.allMarkedAsRead", "Tutte le notifiche segnate come lette"),
      });
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const handleNotificationClick = (notification: UserNotification) => {
    setSelectedNotification(notification);
    setIsModalOpen(true);
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
  };

  const filteredNotifications = notifications.filter((n) => {
    if (filter === "unread") return !n.is_read;
    if (filter === "read") return n.is_read;
    return true;
  });

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return (
    <>
      <SEO 
        title={t("notifications.pageTitle", "Le mie notifiche")} 
        description={t("notifications.pageDescription", "Visualizza tutte le tue notifiche")}
      />
      <Header />
      <div className="min-h-screen bg-background">
        <div className="container max-w-3xl py-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            <Link to="/dashboard" className="hover:text-foreground transition-colors">
              Dashboard
            </Link>
            <span>/</span>
            <span className="text-foreground">{t("notifications.title", "Notifiche")}</span>
          </nav>

          {/* Page Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/dashboard")}
                className="mr-1"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <Bell className="h-6 w-6 text-primary" />
              <h1 className="text-2xl font-bold">
                {t("notifications.title", "Le mie notifiche")}
              </h1>
          {unreadCount > 0 && (
            <span className="bg-primary text-primary-foreground text-xs font-bold px-2 py-1 rounded-full">
              {unreadCount}
            </span>
          )}
        </div>

        {unreadCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={markAllAsRead}
            className="gap-2"
          >
            <CheckCheck className="h-4 w-4" />
            {t("notifications.markAllAsRead", "Segna tutte come lette")}
          </Button>
        )}
      </div>

      {/* Filter Tabs */}
      <Tabs value={filter} onValueChange={(v) => setFilter(v as typeof filter)} className="mb-6">
        <TabsList>
          <TabsTrigger value="all" className="gap-2">
            <Filter className="h-4 w-4" />
            {t("notifications.filterAll", "Tutte")} ({notifications.length})
          </TabsTrigger>
          <TabsTrigger value="unread" className="gap-2">
            {t("notifications.filterUnread", "Non lette")} ({unreadCount})
          </TabsTrigger>
          <TabsTrigger value="read" className="gap-2">
            <Check className="h-4 w-4" />
            {t("notifications.filterRead", "Lette")} ({notifications.length - unreadCount})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Notifications List */}
      <div className="border border-border rounded-lg overflow-hidden bg-card">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground">
            {t("common.loading", "Caricamento...")}
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <Bell className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p>
              {filter === "unread"
                ? t("notifications.noUnread", "Nessuna notifica non letta")
                : filter === "read"
                ? t("notifications.noRead", "Nessuna notifica letta")
                : t("notifications.empty", "Nessuna notifica")}
            </p>
          </div>
        ) : (
          filteredNotifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onClick={() => handleNotificationClick(notification)}
              onToggleRead={() => toggleReadStatus(notification.id, notification.is_read)}
            />
          ))
        )}
      </div>

          {/* Detail Modal */}
          <NotificationDetailModal
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setSelectedNotification(null);
            }}
            notification={
              selectedNotification
                ? {
                    id: selectedNotification.notification.id,
                    title_it: selectedNotification.notification.title_it,
                    title_en: selectedNotification.notification.title_en,
                    title_es: selectedNotification.notification.title_es,
                    description_it: selectedNotification.notification.description_it,
                    description_en: selectedNotification.notification.description_en,
                    description_es: selectedNotification.notification.description_es,
                    image_url: selectedNotification.notification.image_url,
                    cta_url: selectedNotification.notification.cta_url,
                    cta_text_it: selectedNotification.notification.cta_text_it,
                    cta_text_en: selectedNotification.notification.cta_text_en,
                    cta_text_es: selectedNotification.notification.cta_text_es,
                    created_at: selectedNotification.notification.created_at,
                  }
                : null
            }
          />
        </div>
      </div>
      <Footer />
    </>
  );
}
